"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AudioRecordingServiceInstance = void 0;
const client_1 = require("@rws-framework/client");
class AudioRecordingService extends client_1.RWSService {
    constructor() {
        super(...arguments);
        this.mediaRecorder = null;
        this.audioStream = null;
        this.audioChunks = [];
        this.isRecording = false;
        this.recordingOptions = {};
        this.currentMimeType = '';
    }
    /**
     * Check if audio recording is supported
     */
    isRecordingSupported() {
        return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);
    }
    /**
     * Request microphone permission
     */
    async requestMicrophonePermission() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach(track => track.stop()); // Stop the stream immediately
            return true;
        }
        catch (error) {
            console.error('Microphone permission denied:', error);
            return false;
        }
    }
    /**
     * Start audio recording
     */
    async startRecording(options = {}) {
        var _a, _b, _c;
        if (this.isRecording) {
            console.warn('Recording is already in progress');
            return false;
        }
        if (!this.isRecordingSupported()) {
            const error = new Error('Audio recording is not supported in this browser');
            (_a = options.onError) === null || _a === void 0 ? void 0 : _a.call(options, error);
            return false;
        }
        try {
            this.recordingOptions = options;
            this.audioChunks = [];
            // Request microphone access
            this.audioStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });
            // Determine the best MIME type for recording
            this.currentMimeType = this.getBestMimeType();
            // Create MediaRecorder with the chosen format
            this.mediaRecorder = new MediaRecorder(this.audioStream, {
                mimeType: this.currentMimeType
            });
            // Handle data available events
            this.mediaRecorder.ondataavailable = (event) => {
                var _a;
                if (event.data.size > 0) {
                    this.audioChunks.push(event.data);
                    (_a = options.onDataAvailable) === null || _a === void 0 ? void 0 : _a.call(options, event.data);
                }
            };
            // Handle recording stop
            this.mediaRecorder.onstop = () => {
                var _a;
                const finalBlob = new Blob(this.audioChunks, {
                    type: this.currentMimeType
                });
                (_a = options.onRecordingStop) === null || _a === void 0 ? void 0 : _a.call(options, finalBlob);
                this.cleanupRecording();
            };
            // Handle errors
            this.mediaRecorder.onerror = (event) => {
                var _a;
                const error = new Error(`MediaRecorder error: ${event}`);
                console.error('MediaRecorder error:', error);
                (_a = options.onError) === null || _a === void 0 ? void 0 : _a.call(options, error);
                this.cleanupRecording();
            };
            // Start recording
            this.mediaRecorder.start(250); // Collect data every 250ms
            this.isRecording = true;
            (_b = options.onRecordingStart) === null || _b === void 0 ? void 0 : _b.call(options);
            // Auto-stop if specified
            if (options.autoStop && options.autoStop > 0) {
                setTimeout(() => {
                    if (this.isRecording) {
                        this.stopRecording();
                    }
                }, options.autoStop * 1000);
            }
            return true;
        }
        catch (error) {
            console.error('Failed to start recording:', error);
            (_c = options.onError) === null || _c === void 0 ? void 0 : _c.call(options, error);
            this.cleanupRecording();
            return false;
        }
    }
    /**
     * Stop audio recording
     */
    async stopRecording() {
        if (!this.isRecording || !this.mediaRecorder) {
            console.warn('No active recording to stop');
            return null;
        }
        return new Promise((resolve) => {
            const originalOnStop = this.mediaRecorder.onstop;
            this.mediaRecorder.onstop = () => {
                const finalBlob = new Blob(this.audioChunks, {
                    type: this.currentMimeType
                });
                // Call original onstop if it exists
                if (originalOnStop) {
                    originalOnStop.call(this.mediaRecorder, new Event('stop'));
                }
                resolve(finalBlob);
            };
            this.mediaRecorder.stop();
            this.isRecording = false;
        });
    }
    /**
     * Check if currently recording
     */
    isCurrentlyRecording() {
        return this.isRecording;
    }
    /**
     * Get supported MIME types in order of preference
     */
    getBestMimeType() {
        const mimeTypes = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/ogg;codecs=opus',
            'audio/mp4',
            'audio/mpeg'
        ];
        for (const mimeType of mimeTypes) {
            if (MediaRecorder.isTypeSupported(mimeType)) {
                console.log(`Using MIME type: ${mimeType}`);
                return mimeType;
            }
        }
        // Fallback
        console.warn('No preferred MIME type supported, using default');
        return 'audio/webm';
    }
    /**
     * Cleanup recording resources
     */
    cleanupRecording() {
        this.isRecording = false;
        if (this.mediaRecorder) {
            this.mediaRecorder = null;
        }
        if (this.audioStream) {
            this.audioStream.getTracks().forEach(track => track.stop());
            this.audioStream = null;
        }
        this.audioChunks = [];
        this.currentMimeType = '';
    }
}
exports.AudioRecordingServiceInstance = AudioRecordingService;
exports.default = AudioRecordingService.getSingleton();
